export type Theme = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'sendfast-theme';

const THEME_COLOR: Record<ResolvedTheme, string> = {
  light: '#fafaf7',
  dark: '#0f1012',
};

let systemWatchCleanup: (() => void) | null = null;

export function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function resolveTheme(preference: Theme): ResolvedTheme {
  if (preference === 'system') return getSystemTheme();
  return preference;
}

export function getStoredTheme(): Theme | null {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  } catch {
    /* private browsing / blocked storage */
  }
  return null;
}

export function applyResolvedTheme(resolved: ResolvedTheme): void {
  const root = document.documentElement;
  if (resolved === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }

  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) {
    meta.content = THEME_COLOR[resolved];
  }
}

export function applyThemePreference(preference: Theme): ResolvedTheme {
  const resolved = resolveTheme(preference);
  applyResolvedTheme(resolved);
  return resolved;
}

function syncSystemWatch(preference: Theme, onSystemChange: () => void): void {
  systemWatchCleanup?.();
  systemWatchCleanup = null;
  if (preference !== 'system' || typeof window === 'undefined') return;

  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  const handler = () => onSystemChange();
  mq.addEventListener('change', handler);
  systemWatchCleanup = () => mq.removeEventListener('change', handler);
}

export function setStoredTheme(preference: Theme, onSystemChange?: () => void): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    /* ignore */
  }
  applyThemePreference(preference);
  syncSystemWatch(preference, onSystemChange ?? (() => applyThemePreference(preference)));
}

/** Apply saved preference, or follow the OS when unset. */
export function initTheme(onSystemChange?: () => void): Theme {
  const preference = getStoredTheme() ?? 'system';
  applyThemePreference(preference);
  syncSystemWatch(preference, onSystemChange ?? (() => applyThemePreference(preference)));
  return preference;
}
